import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AssignmentPointsSettings,
  AssignmentStatusValue,
  AssignmentStudentStatus,
  AssignmentSubTask,
  GeneralAssignmentItem,
  ProjectAssignmentItem,
  RoutineAssignmentItem,
} from '../../types/assignmentPointsNote';
import { isChallengeAssignment } from '../../types/assignmentPointsNote';
import ChallengeCheckStudentList from '../assignmentChallenge/ChallengeCheckStudentList';
import {
  listAllRoutineDates,
  pointsForStatus,
  upsertStatus,
} from '../../utils/assignmentPointsNoteProgress';
import { classMemberNameMatches, displayClassMemberName } from '../../utils/classMemberNameDisplay';
import {
  buildGeneralAssignmentsExportHtml,
  buildPrintStudents,
  buildProjectAssignmentsExportHtml,
  formatAssignmentDueDateFromIso,
  openAssignmentPrintWindow,
} from '../../utils/assignmentPrintUtils';
import AutoStatusToast from '../common/AutoStatusToast';
import { SeatRotateIcon } from '../common/SeatRotateIcon';
import { useAutoStatusToast } from '../../hooks/useAutoStatusToast';
import type { FlatMember } from './GeneralAssignmentSettingsModal';

const SEAT_EMPTY_TOAST = '자리배치된 멤버가 없습니다. 번호 보기를 사용하세요.';

const STATUS_OPTIONS: {
  value: AssignmentStatusValue;
  label: string;
  color: string;
  num: number;
}[] = [
  { value: 'pending', label: '미확인', color: '#FF316B', num: 1 },
  { value: 'confirmed', label: '확인', color: '#227971', num: 2 },
  { value: 'delayed', label: '지연확인', color: '#65A994', num: 3 },
  { value: 'exempted', label: '예외', color: '#FFCC6E', num: 4 },
];

/** 진행 막대 왼쪽→오른쪽 순서 */
const PROGRESS_BAR_ORDER: AssignmentStatusValue[] = ['exempted', 'delayed', 'confirmed', 'pending'];

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const PER_COL = 5;

function statusColor(status: AssignmentStatusValue): { bg: string; fg: string } {
  if (status === 'submitted') return { bg: '#000000', fg: '#fff' };
  const opt = STATUS_OPTIONS.find((o) => o.value === status);
  if (!opt) return { bg: '#999', fg: '#fff' };
  return { bg: opt.color, fg: opt.value === 'exempted' ? '#1f1703' : '#fff' };
}

/** 받침 유무에 따라 '으로'/'로' (ㄹ 받침은 '로') */
function withEuroParticle(word: string): string {
  const last = word.charCodeAt(word.length - 1);
  if (Number.isNaN(last) || last < 0xac00 || last > 0xd7a3) return `${word}(으)로`;
  const jong = (last - 0xac00) % 28;
  return jong === 0 || jong === 8 ? `${word}로` : `${word}으로`;
}

function formatNum(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDateNav(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getMonth() + 1}월 ${d.getDate()}일 (${DAY_LABELS[d.getDay()]})`;
}

function findBestDateIndex(dates: string[], preferred?: string): number {
  if (!dates.length) return 0;
  if (preferred) {
    const i = dates.indexOf(preferred);
    if (i >= 0) return i;
  }
  const today = new Date();
  const t = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const todayIdx = dates.indexOf(t);
  if (todayIdx >= 0) return todayIdx;
  const future = dates.findIndex((d) => d >= t);
  if (future >= 0) return future;
  return dates.length - 1;
}

function hydrateStatuses(
  assignedIds: string[],
  existing: AssignmentStudentStatus[]
): AssignmentStudentStatus[] {
  const byId = new Map(existing.map((s) => [s.studentId, s]));
  return assignedIds.map((id) => {
    const prev = byId.get(id);
    return (
      prev ?? {
        studentId: id,
        status: 'pending' as const,
        earnedPoints: 0,
      }
    );
  });
}

/** 저장 여부 비교용 — updatedAt 등 무시 */
function statusesFingerprint(
  statuses: AssignmentStudentStatus[],
  memoOverrides?: Record<string, string> | null
): string {
  const rows = statuses
    .map((s) => {
      const rawMemo =
        memoOverrides && Object.prototype.hasOwnProperty.call(memoOverrides, s.studentId)
          ? memoOverrides[s.studentId]
          : s.memo ?? '';
      return {
        id: s.studentId,
        status: s.status,
        pts: Number(s.earnedPoints) || 0,
        memo: String(rawMemo).trim(),
      };
    })
    .sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify(rows);
}

function chunkColumns<T>(items: T[], perCol: number): T[][] {
  const cols: T[][] = [];
  for (let i = 0; i < items.length; i += perCol) {
    cols.push(items.slice(i, i + perCol));
  }
  return cols;
}

export type CheckPanelSelection =
  | { kind: 'general'; item: GeneralAssignmentItem }
  | {
      kind: 'project';
      item: ProjectAssignmentItem;
      subTaskIndex: number;
      onSubTaskChange: (index: number) => void;
    }
  | {
      kind: 'routine';
      item: RoutineAssignmentItem;
      focusDate?: string;
    };

type Props = {
  selection: CheckPanelSelection | null;
  members: FlatMember[];
  /** @deprecated 공유과제는 현재 그룹(members)만 인쇄 — 전달해도 무시됨 */
  printMembers?: FlatMember[];
  settings: AssignmentPointsSettings;
  onApplyStatuses: (payload: {
    kind: 'general' | 'project' | 'routine';
    itemId: string;
    statuses: AssignmentStudentStatus[];
    subTaskId?: string;
    date?: string;
  }) => void | Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
  saveActionsRef?: React.MutableRefObject<{
    save: () => Promise<void>;
  } | null>;
  /** 챌린지 기록 초기화 후 과제노트의 '제출' 표시 정리 */
  onChallengeReset?: (itemId: string, subTaskId: string) => Promise<void> | void;
};

function memosFromStatuses(
  statuses: AssignmentStudentStatus[],
  memberIds: string[]
): Record<string, string> {
  const memos: Record<string, string> = {};
  statuses.forEach((s) => {
    memos[s.studentId] = s.memo || '';
  });
  memberIds.forEach((id) => {
    if (!(id in memos)) memos[id] = '';
  });
  return memos;
}

const AssignmentCheckPanel: React.FC<Props> = ({
  selection,
  members,
  settings,
  onApplyStatuses,
  onDirtyChange,
  saveActionsRef,
  onChallengeReset,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const { toastState, showInfo } = useAutoStatusToast(3000);
  const [studentStatuses, setStudentStatuses] = useState<AssignmentStudentStatus[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<AssignmentStatusValue>('confirmed');
  /** 상단 포인트 행 */
  const [selectedPoints, setSelectedPoints] = useState(0);
  const [pointsUserModified, setPointsUserModified] = useState(false);
  /** 키보드 도우미 포인트 필드 */
  const [pointsInput, setPointsInput] = useState('0');
  const [pointsEnabled, setPointsEnabled] = useState(false);
  const [memoInput, setMemoInput] = useState('');
  const [memoEnabled, setMemoEnabled] = useState(false);
  /** 에듀코디: 메모 열기 → 학생블럭 우측 메모 필드 */
  const [memoViewMode, setMemoViewMode] = useState(false);
  const [memoViewMemos, setMemoViewMemos] = useState<Record<string, string>>({});
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [routineDateIndex, setRoutineDateIndex] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'done'>('idle');
  const [viewMode, setViewMode] = useState<'number' | 'seat'>('number');
  const [isSeatRotated, setIsSeatRotated] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [checkInput, setCheckInput] = useState('');
  const [autoIncrement, setAutoIncrement] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusFieldFocused, setStatusFieldFocused] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const selectionTargetKeyRef = useRef('');
  const dirtyRef = useRef(false);
  const baselineFingerprintRef = useRef('');
  dirtyRef.current = dirty;

  const commitBaseline = useCallback((statuses: AssignmentStudentStatus[]) => {
    baselineFingerprintRef.current = statusesFingerprint(statuses, null);
    setDirty(false);
    setSaveState('done');
    onDirtyChange?.(false);
  }, [onDirtyChange]);

  const basePoints = useMemo(() => {
    if (!selection) return 0;
    if (selection.kind === 'general') return selection.item.basePoints;
    if (selection.kind === 'routine') return selection.item.basePoints;
    const sub = selection.item.subTasks[selection.subTaskIndex];
    return sub?.points ?? 0;
  }, [selection]);

  const assignedIds = useMemo(() => {
    if (!selection) return [] as string[];
    // 현재 학급(members)에 속한 배정 학생만 — 다학급 공유과제도 학급 단위로 카운트
    const inGroup = new Set(members.map((m) => m.id));
    return selection.item.assignedMemberIds.filter((id) => inGroup.has(id));
  }, [selection, members]);

  const assignedMembers = useMemo(() => {
    const byId = new Map(members.map((m) => [m.id, m]));
    return assignedIds.map((id) => byId.get(id)).filter((m): m is FlatMember => m != null);
  }, [assignedIds, members]);

  const routineDates = useMemo(() => {
    if (!selection || selection.kind !== 'routine') return [] as string[];
    return listAllRoutineDates(selection.item);
  }, [selection]);

  /** 챌린지 과제: 학생 블럭 + 제출 카드 + 메모칸 (자리 보기·키보드 도우미 없음) */
  const challengeProjectId =
    selection?.kind === 'project' && isChallengeAssignment(selection.item)
      ? selection.item.challenge.projectId
      : null;

  const currentSubTask: AssignmentSubTask | null =
    selection?.kind === 'project'
      ? selection.item.subTasks[selection.subTaskIndex] ?? null
      : null;

  const currentRoutineDate =
    selection?.kind === 'routine' ? routineDates[routineDateIndex] ?? '' : '';

  const syncPointsForStatus = useCallback(
    (status: AssignmentStatusValue) => {
      setPointsInput(String(pointsForStatus(basePoints, status, settings)));
    },
    [basePoints, settings]
  );

  useEffect(() => {
    if (!selection) {
      selectionTargetKeyRef.current = '';
      baselineFingerprintRef.current = '';
      setStudentStatuses([]);
      setDirty(false);
      setSaveState('idle');
      setSelectedStudents(new Set());
      onDirtyChange?.(false);
      return;
    }
    const targetKey =
      selection.kind === 'general'
        ? `general:${selection.item.id}`
        : selection.kind === 'project'
          ? `project:${selection.item.id}:${selection.subTaskIndex}`
          : `routine:${selection.item.id}:${selection.focusDate ?? ''}`;
    const targetChanged = selectionTargetKeyRef.current !== targetKey;
    selectionTargetKeyRef.current = targetKey;

    const scopedIds = assignedIds;

    const hydrateFromSelection = (): AssignmentStudentStatus[] => {
      if (selection.kind === 'general') {
        return hydrateStatuses(scopedIds, selection.item.studentStatuses);
      }
      if (selection.kind === 'project') {
        const sub = selection.item.subTasks[selection.subTaskIndex];
        return scopedIds.map((mid) => {
          const key = `${sub?.id}:${mid}`;
          return (
            selection.item.studentStatusesBySubTask[key] ?? {
              studentId: mid,
              status: 'pending' as const,
              earnedPoints: 0,
            }
          );
        });
      }
      const dates = listAllRoutineDates(selection.item);
      const idx = findBestDateIndex(dates, selection.focusDate);
      setRoutineDateIndex(idx);
      const date = dates[idx] ?? '';
      const bucket = selection.item.dateStatuses.find((b) => b.date === date);
      return hydrateStatuses(scopedIds, bucket?.studentStatuses ?? []);
    };

    // 저장 직후 item 참조만 바뀐 경우: 편집 중이면 덮어쓰지 않고, 저장완료 표시 유지
    if (!targetChanged) {
      if (!dirtyRef.current) {
        const hydrated = hydrateFromSelection();
        setStudentStatuses(hydrated);
        commitBaseline(hydrated);
        if (challengeProjectId) setMemoViewMemos(memosFromStatuses(hydrated, scopedIds));
      }
      return;
    }

    const hydrated = hydrateFromSelection();
    setStudentStatuses(hydrated);
    commitBaseline(hydrated);
    setSelectedStudents(new Set());
    setCheckInput('');
    setPointsEnabled(false);
    setMemoEnabled(false);
    setMemoInput('');
    setMemoViewMode(!!challengeProjectId);
    setMemoViewMemos(challengeProjectId ? memosFromStatuses(hydrated, scopedIds) : {});
    setKeyboardOpen(false);
    setSelectedStatus('confirmed');
    setSelectedPoints(0);
    setPointsUserModified(false);
    setPointsInput(
      String(
        selection.kind === 'project'
          ? selection.item.subTasks[selection.subTaskIndex]?.points ?? 0
          : selection.item.basePoints
      )
    );
  }, [selection, assignedIds, commitBaseline]); // eslint-disable-line react-hooks/exhaustive-deps

  // 선택 멤버 변경 시 상단 포인트/메모 필드 동기화
  useEffect(() => {
    if (selectedStudents.size === 1) {
      const only = Array.from(selectedStudents)[0];
      const st = studentStatuses.find((s) => s.studentId === only);
      if (!pointsUserModified) setSelectedPoints(st?.earnedPoints ?? 0);
      if (memoViewMode && only in memoViewMemos) {
        setMemoInput(memoViewMemos[only] || '');
      } else {
        setMemoInput(st?.memo ?? '');
      }
    } else if (selectedStudents.size === 0) {
      if (!pointsUserModified) setSelectedPoints(0);
      setMemoInput('');
    } else if (!pointsUserModified) {
      setSelectedPoints(0);
    }
  }, [selectedStudents, studentStatuses, memoViewMode, memoViewMemos]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selection || selection.kind !== 'routine') return;
    const date = routineDates[routineDateIndex];
    if (!date) return;
    const bucket = selection.item.dateStatuses.find((b) => b.date === date);
    const hydrated = hydrateStatuses(assignedIds, bucket?.studentStatuses ?? []);
    setStudentStatuses(hydrated);
    commitBaseline(hydrated);
    setMemoViewMode(false);
    setMemoViewMemos({});
    setSelectedStudents(new Set());
  }, [routineDateIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  /** 현재 편집값이 로드/저장 기준과 다르면 dirty → 저장하기, 같으면 저장완료 */
  useEffect(() => {
    if (!selection || !baselineFingerprintRef.current) return;
    const current = statusesFingerprint(
      studentStatuses,
      memoViewMode ? memoViewMemos : null
    );
    const isDirty = current !== baselineFingerprintRef.current;
    setDirty((prev) => (prev === isDirty ? prev : isDirty));
    onDirtyChange?.(isDirty);
    setSaveState((prev) => {
      if (prev === 'saving') return prev;
      return isDirty ? 'idle' : 'done';
    });
  }, [selection, studentStatuses, memoViewMode, memoViewMemos, onDirtyChange]);

  const pendingCount = useMemo(
    () =>
      studentStatuses.filter((s) => s.status === 'pending' || s.status === 'submitted').length,
    [studentStatuses]
  );

  const statusCounts = useMemo(() => {
    const counts = new Map<AssignmentStatusValue, number>();
    for (const s of studentStatuses) {
      const key: AssignmentStatusValue = s.status === 'submitted' ? 'pending' : s.status;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [studentStatuses]);

  const statusById = useMemo(() => {
    const m = new Map<string, AssignmentStudentStatus>();
    studentStatuses.forEach((s) => m.set(s.studentId, s));
    return m;
  }, [studentStatuses]);

  const resolvePoints = useCallback(
    (status: AssignmentStatusValue): number => {
      if (pointsEnabled && pointsInput !== '' && !Number.isNaN(Number(pointsInput))) {
        return Number(pointsInput);
      }
      return pointsForStatus(basePoints, status, settings);
    },
    [pointsEnabled, pointsInput, basePoints, settings]
  );

  const applyToIds = useCallback(
    (ids: string[], status: AssignmentStatusValue, clearSelection = false) => {
      if (!ids.length) return;
      const pts = resolvePoints(status);
      const now = new Date().toISOString();
      const memoFromHelper = memoEnabled && memoInput.trim() ? memoInput : null;
      if (memoViewMode && memoFromHelper != null) {
        setMemoViewMemos((prev) => {
          const next = { ...prev };
          for (let i = 0; i < ids.length; i += 1) {
            next[ids[i]] = memoFromHelper;
          }
          return next;
        });
      }
      setStudentStatuses((prev) => {
        let next = prev;
        for (let i = 0; i < ids.length; i += 1) {
          const id = ids[i];
          const cur = next.find((s) => s.studentId === id);
          next = upsertStatus(next, {
            studentId: id,
            status,
            earnedPoints: pts,
            memo:
              memoFromHelper != null
                ? memoFromHelper
                : cur?.memo,
            updatedAt: now,
          });
        }
        return next;
      });
      if (memoEnabled) setMemoInput('');
      if (clearSelection) {
        setSelectedStudents(new Set());
        setCheckInput('');
      }
    },
    [resolvePoints, memoEnabled, memoInput, memoViewMode]
  );

  const findMemberByInput = useCallback(
    (raw: string): FlatMember | null => {
      const t = raw.trim();
      if (!t) return null;
      const numMatch = t.match(/^(\d+)/);
      const namePart = t.replace(/^\d+\.\s*/, '').trim();
      // 번호+이름: 그룹별 번호가 겹칠 수 있어 이름과 함께 매칭
      if (numMatch && namePart.length >= 1) {
        const n = Number(numMatch[1]);
        const both = assignedMembers.find(
          (m) =>
            m.orderIndex === n &&
            (classMemberNameMatches(m, namePart, 'exact') || classMemberNameMatches(m, namePart, 'includes'))
        );
        if (both) return both;
      }
      if (namePart.length >= 1) {
        const exact = assignedMembers.find((m) => classMemberNameMatches(m, namePart, 'exact'));
        if (exact) return exact;
        const partial = assignedMembers.find((m) => classMemberNameMatches(m, namePart, 'includes'));
        if (partial) return partial;
      }
      if (numMatch) {
        const n = Number(numMatch[1]);
        const byNum = assignedMembers.find((m) => m.orderIndex === n);
        if (byNum) return byNum;
      }
      return null;
    },
    [assignedMembers]
  );

  const selectMember = useCallback((m: FlatMember) => {
    setSelectedStudents(new Set([m.id]));
    setCheckInput(`${m.orderIndex}. ${displayClassMemberName(m).trim() || '(이름 없음)'}`);
    if (memoViewMode && m.id in memoViewMemos) {
      setMemoInput(memoViewMemos[m.id] || '');
      return;
    }
    const st = statusById.get(m.id);
    if (st?.memo) setMemoInput(st.memo);
    else setMemoInput('');
  }, [statusById, memoViewMode, memoViewMemos]);

  const navigateRelative = useCallback(
    (delta: number) => {
      if (!assignedMembers.length) return;
      let idx = 0;
      if (selectedStudents.size === 1) {
        const only = Array.from(selectedStudents)[0];
        const cur = assignedMembers.findIndex((m) => m.id === only);
        idx = cur >= 0 ? cur : 0;
      } else if (checkInput.trim()) {
        const found = findMemberByInput(checkInput);
        if (found) {
          const cur = assignedMembers.findIndex((m) => m.id === found.id);
          idx = cur >= 0 ? cur : 0;
        }
      }
      const next = Math.min(assignedMembers.length - 1, Math.max(0, idx + delta));
      selectMember(assignedMembers[next]);
    },
    [assignedMembers, selectedStudents, checkInput, findMemberByInput, selectMember]
  );

  const handleHelperSubmit = useCallback(() => {
    const ids =
      selectedStudents.size > 0
        ? Array.from(selectedStudents)
        : (() => {
            const m = findMemberByInput(checkInput);
            return m ? [m.id] : [];
          })();
    if (!ids.length) {
      window.alert('해당하는 멤버를 찾을 수 없습니다.');
      return;
    }
    applyToIds(ids, selectedStatus, !autoIncrement);
    panelRef.current
      ?.querySelector('.cp-apn-kbd-helper')
      ?.setAttribute('data-tutorial-apn-kbd', 'practiced');
    if (autoIncrement) {
      navigateRelative(1);
    }
  }, [
    selectedStudents,
    checkInput,
    findMemberByInput,
    applyToIds,
    selectedStatus,
    autoIncrement,
    navigateRelative,
  ]);

  const handleTabOnTarget = useCallback(() => {
    const m = findMemberByInput(checkInput);
    if (m) {
      selectMember(m);
    } else if (checkInput.trim()) {
      window.alert('해당하는 멤버를 찾을 수 없습니다.');
    }
    window.setTimeout(() => {
      const el = panelRef.current?.querySelector(
        '[data-apn-field="status-input"]'
      ) as HTMLElement | null;
      el?.focus();
    }, 30);
  }, [checkInput, findMemberByInput, selectMember]);

  const focusField = (name: string) => {
    window.setTimeout(() => {
      const el = panelRef.current?.querySelector(
        `[data-apn-field="${name}"]`
      ) as HTMLElement | null;
      el?.focus();
      if (el instanceof HTMLInputElement) el.select();
    }, 30);
  };

  // 1–4: 선택 멤버에 즉시 적용 (입력 포커스 제외)
  useEffect(() => {
    if (!selection) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.key >= '1' && e.key <= '4')) return;
      const active = document.activeElement;
      const tag = active?.tagName;
      const inInput =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        (active as HTMLElement | null)?.isContentEditable;
      if (inInput && !statusFieldFocused) return;

      const idx = Number(e.key) - 1;
      const opt = STATUS_OPTIONS[idx];
      if (!opt) return;
      e.preventDefault();
      e.stopPropagation();

      setSelectedStatus(opt.value);
      syncPointsForStatus(opt.value);

      if (statusFieldFocused) return;
      if (selectedStudents.size === 0) return;
      applyToIds(Array.from(selectedStudents), opt.value, true);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [selection, selectedStudents, statusFieldFocused, applyToIds, syncPointsForStatus]);

  const statusesWithMemoView = useCallback((): AssignmentStudentStatus[] => {
    if (!memoViewMode) return studentStatuses;
    const now = new Date().toISOString();
    return studentStatuses.map((s) => {
      if (!(s.studentId in memoViewMemos)) return s;
      const memo = memoViewMemos[s.studentId].trim();
      return { ...s, memo: memo || undefined, updatedAt: now };
    });
  }, [memoViewMode, memoViewMemos, studentStatuses]);

  const openMemoView = () => {
    const memos: Record<string, string> = {};
    studentStatuses.forEach((s) => {
      memos[s.studentId] = s.memo || '';
    });
    assignedMembers.forEach((m) => {
      if (!(m.id in memos)) memos[m.id] = '';
    });
    setMemoViewMemos(memos);
    setMemoViewMode(true);
  };

  const closeMemoView = () => {
    const now = new Date().toISOString();
    setStudentStatuses((prev) =>
      prev.map((s) => {
        if (!(s.studentId in memoViewMemos)) return s;
        const memo = memoViewMemos[s.studentId].trim();
        const nextMemo = memo || undefined;
        if ((s.memo || '') === (nextMemo || '')) return s;
        return { ...s, memo: nextMemo, updatedAt: now };
      })
    );
    setMemoViewMode(false);
  };

  const handlePrintAssignment = useCallback(() => {
    if (!selection || isPrinting) return;
    setIsPrinting(true);
    try {
      const statuses = statusesWithMemoView();
      // 공유과제: 현재 그룹에 속한 배정 멤버만 인쇄
      const printIds = assignedMembers.map((m) => m.id);
      const nameLookup = assignedMembers;
      if (printIds.length === 0) {
        alert('현재 그룹에 인쇄할 배정 멤버가 없습니다.');
        return;
      }
      if (selection.kind === 'project') {
        const item = selection.item;
        const activeSub = item.subTasks[selection.subTaskIndex];
        const subTasksData = item.subTasks.map((sub) => {
          const fromMap = printIds.map((sid) => {
            const key = `${sub.id}:${sid}`;
            return (
              item.studentStatusesBySubTask[key] ?? {
                studentId: sid,
                status: 'pending' as const,
                earnedPoints: 0,
              }
            );
          });
          const useStatuses =
            activeSub && sub.id === activeSub.id
              ? statuses.filter((s) => printIds.includes(s.studentId))
              : fromMap;
          return {
            subTaskTitle: sub.title || '하위과제',
            subTaskPoints: sub.points || 0,
            students: buildPrintStudents(printIds, useStatuses, nameLookup),
          };
        });
        const dueText = formatAssignmentDueDateFromIso(item.dueDate) || '마감일 없음';
        const totalPoints = subTasksData.reduce((sum, st) => sum + st.subTaskPoints, 0);
        const rewardCoins = item.rewardEnabled ? item.rewardCoins || 0 : 0;
        const rewardItemCardCount = item.rewardEnabled
          ? item.rewardItemIds?.length || 0
          : 0;
        const html = buildProjectAssignmentsExportHtml([
          {
            project: item,
            dueDateText: dueText,
            basePoints: totalPoints,
            rewardCoins,
            rewardItemCardCount,
            subTasks: subTasksData,
          },
        ]);
        openAssignmentPrintWindow(html);
        return;
      }

      let dueDateText = '마감일 없음';
      let categoryName = '전체 카테고리';
      let basePts = 0;
      const item = selection.item;
      if (selection.kind === 'routine') {
        const dateIso = routineDates[routineDateIndex] || '';
        dueDateText = dateIso
          ? `과제일 ${formatAssignmentDueDateFromIso(dateIso)}`
          : '마감일 없음';
        categoryName =
          settings.categories.find((c) => c.id === item.categoryId)?.name || '루틴 과제';
        basePts = item.basePoints || 0;
      } else {
        dueDateText = formatAssignmentDueDateFromIso(item.dueDate) || '마감일 없음';
        categoryName =
          settings.categories.find((c) => c.id === item.categoryId)?.name || '전체 카테고리';
        basePts = item.basePoints || 0;
      }
      const rewardCoins = item.rewardEnabled ? item.rewardCoins || 0 : 0;
      const rewardItemCardCount = item.rewardEnabled ? item.rewardItemIds?.length || 0 : 0;
      const html = buildGeneralAssignmentsExportHtml([
        {
          assignment: item,
          categoryName,
          dueDateText,
          basePoints: basePts,
          rewardCoins,
          rewardItemCardCount,
          students: buildPrintStudents(
            printIds,
            statuses.filter((s) => printIds.includes(s.studentId)),
            nameLookup
          ),
        },
      ]);
      openAssignmentPrintWindow(html);
    } catch (error) {
      console.error('과제 프린트 실패:', error);
      alert('프린트에 실패했습니다.');
    } finally {
      setIsPrinting(false);
    }
  }, [
    selection,
    isPrinting,
    statusesWithMemoView,
    assignedMembers,
    routineDates,
    routineDateIndex,
    settings.categories,
  ]);

  /** 수업기록 모달 붙여넣기용: `#이름 메모` (이름 뒤 공백으로 태그 칩 확정) */
  const handleCopyMemosForClassRecord = useCallback(async () => {
    const statuses = statusesWithMemoView();
    const byId = new Map(statuses.map((s) => [s.studentId, s]));
    const lines: string[] = [];
    for (const m of assignedMembers) {
      const name = displayClassMemberName(m).trim().replace(/\s+/g, '');
      if (!name) continue;
      const memo = (byId.get(m.id)?.memo ?? '').replace(/\s+/g, ' ').trim();
      lines.push(memo ? `#${name} ${memo}` : `#${name} `);
    }
    const text = lines.join('\n');
    if (!text.trim()) {
      showInfo('복사할 멤버가 없습니다.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      showInfo('과제에 속한 멤버 이름과 멤버별 메모를 클립보드에 복사하였습니다.');
    } catch {
      showInfo('클립보드 복사에 실패했습니다.');
    }
  }, [assignedMembers, showInfo, statusesWithMemoView]);

  const handleSave = async () => {
    if (!selection || !dirty || saveState === 'saving') return;
    const statuses = statusesWithMemoView();
    setSaveState('saving');
    try {
      if (selection.kind === 'general') {
        await onApplyStatuses({ kind: 'general', itemId: selection.item.id, statuses });
      } else if (selection.kind === 'project' && currentSubTask) {
        await onApplyStatuses({
          kind: 'project',
          itemId: selection.item.id,
          statuses,
          subTaskId: currentSubTask.id,
        });
      } else if (selection.kind === 'routine' && currentRoutineDate) {
        await onApplyStatuses({
          kind: 'routine',
          itemId: selection.item.id,
          statuses,
          date: currentRoutineDate,
        });
      } else {
        setSaveState('idle');
        return;
      }
      if (memoViewMode) {
        setStudentStatuses(statuses);
      }
      commitBaseline(statuses);
    } catch (err) {
      setSaveState('idle');
      throw err;
    }
  };

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    return () => {
      onDirtyChange?.(false);
    };
  }, [onDirtyChange]);

  useEffect(() => {
    if (!saveActionsRef) return undefined;
    saveActionsRef.current = { save: handleSave };
    return () => {
      saveActionsRef.current = null;
    };
  }, [saveActionsRef, handleSave]);

  const clearSelectedMemos = () => {
    if (selectedStudents.size === 0) return;
    if (memoViewMode) {
      setMemoViewMemos((prev) => {
        const next = { ...prev };
        selectedStudents.forEach((id) => {
          next[id] = '';
        });
        return next;
      });
    }
    setStudentStatuses((prev) =>
      prev.map((s) =>
        selectedStudents.has(s.studentId) ? { ...s, memo: undefined, updatedAt: new Date().toISOString() } : s
      )
    );
  };

  const handleApplyPointsAndMemo = () => {
    if (selectedStudents.size === 0) return;
    const applyPts =
      !(selectedStudents.size > 1 && selectedPoints === 0 && !pointsUserModified);
    const memoText = memoInput.trim();
    const memoValue = memoText || undefined;
    const now = new Date().toISOString();
    const selectedIds = Array.from(selectedStudents);

    if (memoViewMode) {
      setMemoViewMemos((prev) => {
        const next = { ...prev };
        selectedIds.forEach((id) => {
          next[id] = memoText;
        });
        return next;
      });
    }

    setStudentStatuses((prev) => {
      let next = prev;
      selectedIds.forEach((id) => {
        const cur = next.find((s) => s.studentId === id);
        next = upsertStatus(next, {
          studentId: id,
          status: cur?.status ?? 'pending',
          earnedPoints: applyPts ? selectedPoints : cur?.earnedPoints ?? 0,
          memo: memoValue,
          updatedAt: now,
        });
      });
      return next;
    });
    setMemoInput('');
    setSelectedStudents(new Set());
    setCheckInput('');
    setPointsUserModified(false);
  };

  const isCommonPointsPlaceholder =
    selectedStudents.size > 1 && selectedPoints === 0 && !pointsUserModified;
  const pointsFieldDisplay =
    selectedStudents.size === 0 || isCommonPointsPlaceholder ? '' : String(selectedPoints);

  /** 에듀코디 AssignmentCheckModal seatViewData와 동일: 16트랙·span2·빈 트랙 압축 */
  const seatViewData = useMemo(() => {
    const assignedIdSet = new Set(assignedIds);
    const seated: Array<{
      member: FlatMember;
      seatRow: number;
      seatCol: number;
      isAssigned: boolean;
    }> = [];
    const unseated: Array<{ member: FlatMember; isAssigned: boolean }> = [];

    members.forEach((member) => {
      const isAssigned = assignedIdSet.has(member.id);
      const pos = member.seatPosition;
      if (pos && typeof pos.row === 'number' && typeof pos.col === 'number') {
        seated.push({ member, seatRow: pos.row, seatCol: pos.col, isAssigned });
      } else {
        unseated.push({ member, isAssigned });
      }
    });

    if (seated.length === 0) {
      return {
        positioned: [] as Array<{
          member: FlatMember;
          isAssigned: boolean;
          displayRow: number;
          displayCol: number;
        }>,
        unseated,
        rowTemplate: '',
        colTemplate: '',
      };
    }

    const GRID_TRACKS = 16;
    const ROTATE_MAX_INDEX = 14;
    const rotated = seated.map((item) => ({
      ...item,
      seatRow: isSeatRotated ? ROTATE_MAX_INDEX - item.seatRow : item.seatRow,
      seatCol: isSeatRotated ? ROTATE_MAX_INDEX - item.seatCol : item.seatCol,
    }));

    const usedRows = new Set<number>();
    const usedCols = new Set<number>();
    rotated.forEach(({ seatRow, seatCol }) => {
      if (seatRow >= 0 && seatRow < GRID_TRACKS) usedRows.add(seatRow);
      [seatCol, seatCol + 1].forEach((col) => {
        if (col >= 0 && col < GRID_TRACKS) usedCols.add(col);
      });
    });

    const trimStartRow = Math.min(...Array.from(usedRows));
    const trimEndRow = Math.max(...Array.from(usedRows));
    const trimStartCol = Math.min(...Array.from(usedCols));
    const trimEndCol = Math.max(...Array.from(usedCols));

    const rowCount = Math.max(1, trimEndRow - trimStartRow + 1);
    const colCount = Math.max(1, trimEndCol - trimStartCol + 1);

    const rowTemplate = Array.from({ length: rowCount }, (_, idx) =>
      usedRows.has(trimStartRow + idx) ? '40px' : '10px'
    ).join(' ');
    const colTemplate = Array.from({ length: colCount }, (_, idx) =>
      usedCols.has(trimStartCol + idx) ? '1fr' : '0.35fr'
    ).join(' ');

    const positioned = rotated.map((item) => ({
      member: item.member,
      isAssigned: item.isAssigned,
      displayRow: item.seatRow - trimStartRow,
      displayCol: item.seatCol - trimStartCol,
    }));

    return { positioned, unseated, rowTemplate, colTemplate };
  }, [members, assignedIds, isSeatRotated]);

  const hasSeatedMembers = seatViewData.positioned.length > 0;

  const enterSeatMode = useCallback(() => {
    if (!hasSeatedMembers) {
      showInfo(SEAT_EMPTY_TOAST);
      setViewMode('number');
      return;
    }
    setViewMode('seat');
  }, [hasSeatedMembers, showInfo]);

  const toggleViewMode = useCallback(() => {
    if (viewMode === 'seat') {
      setViewMode('number');
      return;
    }
    enterSeatMode();
  }, [viewMode, enterSeatMode]);

  useEffect(() => {
    if (viewMode !== 'seat') return;
    if (hasSeatedMembers) return;
    setViewMode('number');
    showInfo(SEAT_EMPTY_TOAST);
  }, [viewMode, hasSeatedMembers, showInfo]);

  const seatUnassignedColumns = useMemo(() => {
    const list = seatViewData.unseated.filter((x) => x.isAssigned).map((x) => x.member);
    return chunkColumns(list, PER_COL);
  }, [seatViewData.unseated]);

  const numberColumns = useMemo(
    () => chunkColumns(assignedMembers, PER_COL),
    [assignedMembers]
  );

  const memberHasMemo = (memberId: string): boolean => {
    if (memoViewMode) return Boolean(memoViewMemos[memberId]?.trim());
    return Boolean(statusById.get(memberId)?.memo?.trim());
  };

  const clearStudentSelectionIfBackground = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    // 학생 블럭·영역 안 컨트롤(메모/회전 등) 클릭은 유지
    if (
      target.closest(
        '.cp-apn-student-card, button, input, textarea, select, a, [role="button"]'
      )
    ) {
      return;
    }
    if (selectedStudents.size === 0) return;
    setSelectedStudents(new Set());
    setCheckInput('');
  };

  const renderStudentCard = (
    m: FlatMember,
    opts?: { isAssigned?: boolean; extraSide?: React.ReactNode; className?: string }
  ) => {
    const isAssigned = opts?.isAssigned !== false;
    const st = statusById.get(m.id);
    const status = st?.status ?? 'pending';
    const colors = isAssigned
      ? statusColor(status)
      : { bg: 'var(--color-surface-highlight, #eee)', fg: '#999' };
    const selected = selectedStudents.has(m.id);
    const hasMemo = isAssigned && memberHasMemo(m.id);
    return (
      <button
        data-sfx="adjust"
        key={m.id}
        type="button"
        className={`cp-apn-student-card${selected ? ' is-selected' : ''}${
          isAssigned ? '' : ' is-unassigned'
        }${isAssigned && status === 'exempted' ? ' is-light-status' : ''}${
          opts?.className ? ` ${opts.className}` : ''
        }`}
        disabled={!isAssigned}
        onClick={() => {
          if (!isAssigned) return;
          setPointsUserModified(false);
          setSelectedStudents((prev) => {
            const next = new Set(prev);
            if (next.has(m.id)) next.delete(m.id);
            else next.add(m.id);
            return next;
          });
          setCheckInput(`${m.orderIndex}. ${displayClassMemberName(m).trim() || '(이름 없음)'}`);
          if (st?.memo) setMemoInput(st.memo);
        }}
        onDoubleClick={() => {
          if (!isAssigned) return;
          applyToIds([m.id], selectedStatus, true);
        }}
        title={isAssigned ? st?.memo || undefined : '과제가 할당되지 않음'}
        style={
          isAssigned
            ? ({
                ['--cp-apn-status' as string]: colors.bg,
                ['--cp-apn-status-on' as string]: colors.fg,
              } as React.CSSProperties)
            : undefined
        }
      >
        {selected ? <span className="cp-apn-student-card-overlay" aria-hidden /> : null}
        <span className="cp-apn-student-card-row">
          <span className="cp-apn-student-label">
            <span className="cp-apn-student-num">{formatNum(m.orderIndex)}</span>
            <span className="cp-apn-student-name">{displayClassMemberName(m).trim() || '(이름 없음)'}</span>
          </span>
          <span className="cp-apn-student-card-side">
            {opts?.extraSide}
            {hasMemo ? <span className="cp-apn-student-memo-badge">M</span> : null}
            <span className="cp-apn-student-card-pts">{isAssigned ? st?.earnedPoints ?? 0 : 0}pt</span>
          </span>
        </span>
      </button>
    );
  };

  if (!selection) {
    return (
      <div className="cp-apn-check-panel cp-apn-check-panel--empty">
        <header className="cp-apn-section-head">
          <span className="cp-apn-section-head-title">과제 체크</span>
        </header>
        <div className="cp-apn-section-body cp-apn-check-body">
          <p>왼쪽에서 과제를 선택하면 체크 패널이 열립니다.</p>
        </div>
      </div>
    );
  }

  const title = selection.item.title?.trim() || '(제목 없음)';
  const navLabel =
    selection.kind === 'project' && selection.item.subTasks.length > 0
      ? `${currentSubTask?.title ?? '하위과제'} (${selection.subTaskIndex + 1}/${selection.item.subTasks.length})`
      : selection.kind === 'routine'
        ? currentRoutineDate
          ? `${formatDateNav(currentRoutineDate)} (${routineDateIndex + 1}/${routineDates.length})`
          : '날짜 없음'
        : '';
  const showNav =
    (selection.kind === 'project' && selection.item.subTasks.length > 0) ||
    selection.kind === 'routine';
  const canNavPrev =
    selection.kind === 'project'
      ? selection.subTaskIndex > 0
      : selection.kind === 'routine'
        ? routineDateIndex > 0 && routineDates.length > 0
        : false;
  const canNavNext =
    selection.kind === 'project'
      ? selection.subTaskIndex < selection.item.subTasks.length - 1
      : selection.kind === 'routine'
        ? routineDateIndex < routineDates.length - 1 && routineDates.length > 0
        : false;

  const step = settings.statusDefaultPoints.confirmed || 1;
  const selectedStatusLabel =
    STATUS_OPTIONS.find((o) => o.value === selectedStatus)?.label ?? '';

  const confirmLeaveNav = () =>
    !dirty || window.confirm('저장하지 않은 변경이 있습니다. 이동할까요?');

  const handleNavPrev = () => {
    if (!confirmLeaveNav()) return;
    if (selection.kind === 'project') {
      selection.onSubTaskChange(selection.subTaskIndex - 1);
    } else if (selection.kind === 'routine') {
      setRoutineDateIndex((i) => Math.max(0, i - 1));
    }
  };

  const handleNavNext = () => {
    if (!confirmLeaveNav()) return;
    if (selection.kind === 'project') {
      selection.onSubTaskChange(selection.subTaskIndex + 1);
    } else if (selection.kind === 'routine') {
      setRoutineDateIndex((i) => Math.min(routineDates.length - 1, i + 1));
    }
  };

  return (
    <div className="cp-apn-check-panel" ref={panelRef}>
      <header
        className={`cp-apn-section-head cp-apn-check-section-head${
          showNav ? ' has-nav' : ''
        }${navLabel ? ' has-extra' : ''}`}
      >
        <div className="cp-apn-check-head-main">
          {showNav ? (
            <div className="cp-apn-check-head-nav" aria-label="과제 이동">
              <button
                type="button"
                className="cp-apn-check-head-nav-btn"
                disabled={!canNavPrev}
                aria-label="이전"
                onClick={handleNavPrev}
              >
                ‹
              </button>
              <button
                type="button"
                className="cp-apn-check-head-nav-btn"
                disabled={!canNavNext}
                aria-label="다음"
                onClick={handleNavNext}
              >
                ›
              </button>
            </div>
          ) : null}
          <div className="cp-apn-check-head-text">
            <h2 className="cp-apn-check-head-title">{title}</h2>
            <div className="cp-apn-check-head-desc">
              <p className="cp-apn-check-meta">
                기본 {basePoints}pt · 미확인 {pendingCount}/{assignedIds.length}명
                {dirty ? ' · 수정됨' : ''}
              </p>
              {navLabel ? (
                <p className="cp-apn-check-meta-extra">{navLabel}</p>
              ) : null}
            </div>
          </div>
        </div>
        <div className="cp-apn-check-head-actions">
          <button
            type="button"
            className="cp-apn-icon-action-btn"
            onClick={handlePrintAssignment}
            disabled={isPrinting}
            title="프린트"
            aria-label="프린트"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                stroke="currentColor"
                d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z"
              />
            </svg>
          </button>
          <button
            type="button"
            className="cp-apn-icon-action-btn"
            onClick={() => {
              void handleCopyMemosForClassRecord();
            }}
            title="멤버 이름·메모 복사"
            aria-label="멤버 이름과 메모를 클립보드에 복사"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                stroke="currentColor"
                d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
              />
            </svg>
          </button>
          <button
            type="button"
            className={`cp-apn-btn cp-apn-btn-primary cp-apn-check-head-save${
              saveState === 'done' && !dirty ? ' is-saved' : ''
            }`}
            disabled={!dirty || saveState === 'saving'}
            onClick={() => {
              void handleSave().catch(() => undefined);
            }}
          >
            {saveState === 'saving'
              ? '저장 중…'
              : saveState === 'done' && !dirty
                ? '저장완료'
                : '저장하기'}
          </button>
        </div>
      </header>

      <div className="cp-apn-check-progress" aria-label="과제 상태 현황">
        <div className="cp-apn-check-progress-bar" aria-hidden>
          {studentStatuses.length > 0
            ? PROGRESS_BAR_ORDER.map((value) => STATUS_OPTIONS.find((o) => o.value === value)!).map((opt) => {
                const n = statusCounts.get(opt.value) ?? 0;
                if (n === 0) return null;
                return (
                  <span
                    key={opt.value}
                    className="cp-apn-check-progress-seg"
                    style={{ width: `${(n / studentStatuses.length) * 100}%`, background: opt.color }}
                  />
                );
              })
            : null}
        </div>
        <div className="cp-apn-check-progress-legend">
          {STATUS_OPTIONS.map((opt) => (
            <span key={opt.value} className="cp-apn-check-progress-item">
              <span className="cp-apn-check-progress-dot" style={{ background: opt.color }} />
              {opt.label} {statusCounts.get(opt.value) ?? 0}
            </span>
          ))}
          <span className="cp-apn-check-progress-total">
            완료 {studentStatuses.length - pendingCount} / {studentStatuses.length}명
          </span>
        </div>
      </div>

      <div className="cp-apn-section-body cp-apn-check-body">
      <section className="cp-apn-check-section">
        <div className="cp-apn-status-btns" role="group" aria-label="과제 상태">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`cp-apn-status-btn${selectedStatus === opt.value ? ' is-active' : ''}`}
              style={{ ['--cp-apn-status-color' as string]: opt.color } as React.CSSProperties}
              onClick={() => {
                setSelectedStatus(opt.value);
                syncPointsForStatus(opt.value);
                if (selectedStudents.size > 0) {
                  applyToIds(Array.from(selectedStudents), opt.value, true);
                }
              }}
            >
              <span
                className="cp-apn-status-dot"
                style={{
                  background: opt.color,
                  color: opt.value === 'exempted' ? '#1f1703' : '#fff',
                }}
              >
                {opt.num}
              </span>
              <span className="cp-apn-status-label">{opt.label}</span>
            </button>
          ))}
        </div>

        <div className="cp-apn-composer">
          <div className="cp-apn-composer-stepper">
            <input
              className="cp-apn-points-field"
              type="number"
              min={0}
              value={pointsFieldDisplay}
              placeholder={isCommonPointsPlaceholder ? '공통 포인트' : '포인트'}
              aria-label="포인트"
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') {
                  setSelectedPoints(0);
                  setPointsUserModified(true);
                  return;
                }
                const n = Number(v);
                if (!Number.isNaN(n) && n >= 0) {
                  setSelectedPoints(n);
                  setPointsUserModified(true);
                }
              }}
              onFocus={(e) => {
                if (selectedStudents.size <= 1) e.target.select();
              }}
            />
            <button
              type="button"
              className="cp-apn-sq-btn"
              title="포인트 0으로 설정"
              onClick={() => {
                setSelectedPoints(0);
                setPointsUserModified(true);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
            <button
              type="button"
              className="cp-apn-sq-btn"
              title="포인트 감소"
              onClick={() => {
                setSelectedPoints((p) => Math.max(0, p - step));
                setPointsUserModified(true);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M19 13H5v-3h14v3z" fill="currentColor" />
              </svg>
            </button>
            <button
              type="button"
              className="cp-apn-sq-btn"
              title="포인트 증가"
              onClick={() => {
                setSelectedPoints((p) => p + step);
                setPointsUserModified(true);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M19 13h-6v6h-3v-6H5v-3h6V5h3v6h6v3z" fill="currentColor" />
              </svg>
            </button>
          </div>
          <input
            className="cp-apn-memo-field"
            type="text"
            placeholder={selectedStudents.size > 1 ? '공통 메모 (선택)' : '메모 입력 (선택)'}
            value={memoInput}
            onChange={(e) => setMemoInput(e.target.value)}
          />
          <button
            type="button"
            className="cp-apn-apply-btn"
            title="포인트·메모 적용"
            onClick={handleApplyPointsAndMemo}
          >
            선택 학생에 적용
          </button>
        </div>

        <div className="cp-apn-select-bar">
          {!memoViewMode ? (
            <div
              className={`cp-apn-view-toggle${viewMode === 'seat' ? ' is-seat' : ''}`}
              role="button"
              tabIndex={0}
              aria-label="번호 자리 보기 전환"
              onClick={toggleViewMode}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleViewMode();
                }
              }}
            >
              <span className={viewMode === 'number' ? 'is-on' : ''}>번호</span>
              <span className={viewMode === 'seat' ? 'is-on' : ''}>자리</span>
            </div>
          ) : null}
          <button
            type="button"
            className="cp-apn-mini-btn"
            onClick={() => setSelectedStudents(new Set(assignedIds))}
          >
            전체 선택
          </button>
          <button
            type="button"
            className="cp-apn-mini-btn"
            onClick={() => {
              setSelectedStudents(new Set());
              setCheckInput('');
            }}
          >
            선택 해제
          </button>
          <button
            type="button"
            className="cp-apn-mini-btn"
            disabled={selectedStudents.size === 0}
            onClick={clearSelectedMemos}
          >
            선택 메모 삭제
          </button>
          {!challengeProjectId ? (
            <button
              type="button"
              className="cp-apn-mini-btn"
              onClick={() => {
                if (memoViewMode) closeMemoView();
                else openMemoView();
              }}
            >
              {memoViewMode ? '메모 닫기' : '메모 열기'}
            </button>
          ) : null}
        </div>

        {challengeProjectId && currentSubTask && selection.kind === 'project' ? (
          <div className="cp-apn-student-area" onClick={clearStudentSelectionIfBackground}>
            <ChallengeCheckStudentList
              projectId={challengeProjectId}
              themeColor={selection?.item.color}
              subTaskId={currentSubTask.id}
              subTaskTitle={currentSubTask.title}
              members={assignedMembers}
              renderHead={(memberId, extraSide) => {
                const m = assignedMembers.find((x) => x.id === memberId);
                return m ? renderStudentCard(m, { extraSide, className: 'cp-chl-check-head-btn' }) : null;
              }}
              displayName={(memberId) => {
                const m = assignedMembers.find((x) => x.id === memberId);
                return m ? displayClassMemberName(m).trim() || '(이름 없음)' : '';
              }}
              statusColorOf={(memberId) =>
                statusColor(statusById.get(memberId)?.status ?? 'pending').bg
              }
              memos={memoViewMemos}
              onMemoChange={(memberId, value) =>
                setMemoViewMemos((prev) => ({ ...prev, [memberId]: value }))
              }
              onReset={async () => {
                const wasDirty = dirtyRef.current;
                await onChallengeReset?.(selection.item.id, currentSubTask.id);
                if (!wasDirty) return;
                // 저장 전 편집 중이면 화면 상태도 미확인으로 되돌림(메모는 유지)
                const now = new Date().toISOString();
                setStudentStatuses((prev) =>
                  prev.map((st) =>
                    st.status === 'pending' && !st.earnedPoints
                      ? st
                      : { ...st, status: 'pending', earnedPoints: 0, updatedAt: now }
                  )
                );
              }}
            />
          </div>
        ) : memoViewMode ? (
          <div
            className="cp-apn-memo-view-layout cp-apn-student-area"
            onClick={clearStudentSelectionIfBackground}
          >
            {assignedMembers.map((m) => {
              const st = statusById.get(m.id);
              const currentMemo = memoViewMemos[m.id] ?? st?.memo ?? '';
              return (
                <div key={m.id} className="cp-apn-memo-view-row">
                  <div className="cp-apn-memo-view-student">{renderStudentCard(m)}</div>
                  <div className="cp-apn-memo-view-field-wrap">
                    <textarea
                      className="cp-apn-memo-view-field"
                      data-memo-student-id={m.id}
                      rows={1}
                      value={currentMemo}
                      placeholder="메모"
                      onChange={(e) => {
                        const value = e.target.value;
                        setMemoViewMemos((prev) => ({ ...prev, [m.id]: value }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
                        e.preventDefault();
                        const textareas = Array.from(
                          panelRef.current?.querySelectorAll<HTMLTextAreaElement>(
                            'textarea[data-memo-student-id]'
                          ) ?? []
                        );
                        const idx = textareas.findIndex(
                          (el) => el.getAttribute('data-memo-student-id') === m.id
                        );
                        if (idx >= 0 && idx + 1 < textareas.length) {
                          window.setTimeout(() => textareas[idx + 1].focus(), 0);
                        }
                      }}
                    />
                  </div>
                </div>
              );
            })}
            {assignedMembers.length === 0 ? (
              <p className="cp-apn-empty-hint">배정된 멤버가 없습니다.</p>
            ) : null}
          </div>
        ) : viewMode === 'number' ? (
          <div
            className="cp-apn-student-grid cp-apn-student-area"
            data-num-columns={numberColumns.length}
            onClick={clearStudentSelectionIfBackground}
          >
            {numberColumns.map((col, ci) => (
              <div key={ci} className="cp-apn-student-col">
                {col.map((m) => renderStudentCard(m))}
              </div>
            ))}
            {assignedMembers.length === 0 ? (
              <p className="cp-apn-empty-hint">배정된 멤버가 없습니다.</p>
            ) : null}
          </div>
        ) : (
          <div
            className="cp-apn-seat-wrap cp-apn-student-area"
            onClick={clearStudentSelectionIfBackground}
          >
            {seatViewData.positioned.length > 0 ? (
              <>
                <div className="cp-apn-seat-scroll">
                  <div
                    className="cp-apn-seat-grid"
                    style={{
                      gridTemplateColumns: seatViewData.colTemplate,
                      gridTemplateRows: seatViewData.rowTemplate,
                    }}
                  >
                    {seatViewData.positioned.map(({ member, isAssigned, displayRow, displayCol }) => (
                      <div
                        key={member.id}
                        className="cp-apn-seat-cell"
                        style={{
                          gridRow: displayRow + 1,
                          gridColumn: `${displayCol + 1} / span 2`,
                        }}
                      >
                        {renderStudentCard(member, { isAssigned })}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="cp-apn-seat-rotate-wrap">
                  <button
                    type="button"
                    className="cp-apn-seat-rotate-btn"
                    title="180도 회전"
                    aria-label="자리 배치 180도 회전"
                    onClick={() => setIsSeatRotated((v) => !v)}
                  >
                    <span className="cp-apn-seat-rotate-arrows" aria-hidden>
                      <SeatRotateIcon size={14} />
                    </span>
                    <span>180</span>
                  </button>
                </div>
              </>
            ) : null}
            {seatUnassignedColumns.length > 0 ? (
              <>
                <p className="cp-apn-seat-unassigned-title">자리 미지정 멤버</p>
                <div
                  className="cp-apn-student-grid"
                  data-num-columns={seatUnassignedColumns.length}
                >
                  {seatUnassignedColumns.map((col, ci) => (
                    <div key={ci} className="cp-apn-student-col">
                      {col.map((m) => renderStudentCard(m))}
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        )}
      </section>

      {!challengeProjectId ? (
      <section className={`cp-apn-kbd-section${keyboardOpen ? ' is-open' : ''}`}>
        <button
          type="button"
          className="cp-apn-kbd-toggle"
          aria-expanded={keyboardOpen}
          onClick={() => setKeyboardOpen((v) => !v)}
        >
          <span className="cp-apn-kbd-toggle-icon" aria-hidden>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="2.5" y="6" width="19" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
              <path
                d="M6.5 10h1M10 10h1M13.5 10h1M17 10h.5M7 14h10"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className="cp-apn-kbd-toggle-title">키보드 과제 체크 도우미</span>
          <span className="cp-apn-kbd-toggle-desc">번호만 입력해 빠르게 체크</span>
          <span className="cp-apn-kbd-toggle-pill">
            {keyboardOpen ? '닫기' : '열기'}
            <span className="cp-apn-kbd-toggle-caret" aria-hidden>
              {keyboardOpen ? '▲' : '▼'}
            </span>
          </span>
        </button>

        {keyboardOpen ? (
          <div className="cp-apn-kbd-helper">
            <div className="cp-apn-kbd-fields">
              <div className="cp-apn-kbd-field">
                <span className="cp-apn-kbd-label">대상</span>
                <input
                  data-apn-field="target-input"
                  tabIndex={1}
                  value={checkInput}
                  disabled={selectedStudents.size > 1}
                  placeholder="번호 또는 이름"
                  onChange={(e) => setCheckInput(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
                      e.preventDefault();
                      handleTabOnTarget();
                    }
                  }}
                />
              </div>
              <div className="cp-apn-kbd-field">
                <span className="cp-apn-kbd-label">과제 상태</span>
                <div className="cp-apn-kbd-status-wrap">
                  <button
                    type="button"
                    className="cp-apn-kbd-status-btn"
                    data-apn-field="status-input"
                    tabIndex={2}
                    onFocus={() => setStatusFieldFocused(true)}
                    onBlur={() => setStatusFieldFocused(false)}
                    onClick={() => setStatusDropdownOpen((v) => !v)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        focusField(
                          pointsEnabled ? 'points-input' : memoEnabled ? 'memo-input' : 'submit-button'
                        );
                      }
                    }}
                  >
                    <span
                      className="cp-apn-status-dot"
                      style={{
                        background: STATUS_OPTIONS.find((o) => o.value === selectedStatus)?.color,
                        color: selectedStatus === 'exempted' ? '#1f1703' : '#fff',
                      }}
                    >
                      {STATUS_OPTIONS.find((o) => o.value === selectedStatus)?.num}
                    </span>
                    <span className="cp-apn-kbd-status-text">
                      {STATUS_OPTIONS.find((o) => o.value === selectedStatus)?.label}
                    </span>
                  </button>
                  {statusDropdownOpen ? (
                    <div className="cp-apn-kbd-status-drop">
                      {STATUS_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            setSelectedStatus(opt.value);
                            syncPointsForStatus(opt.value);
                            setStatusDropdownOpen(false);
                          }}
                        >
                          <span
                            className="cp-apn-status-dot"
                            style={{
                              background: opt.color,
                              color: opt.value === 'exempted' ? '#1f1703' : '#fff',
                            }}
                          >
                            {opt.num}
                          </span>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="cp-apn-kbd-field">
                <span className="cp-apn-kbd-label">포인트</span>
                <input
                  data-apn-field="points-input"
                  tabIndex={3}
                  type="text"
                  value={pointsInput}
                  disabled={!pointsEnabled}
                  onChange={(e) => setPointsInput(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      focusField(memoEnabled ? 'memo-input' : 'submit-button');
                    }
                  }}
                />
              </div>
            </div>
            {memoEnabled ? (
              <input
                className="cp-apn-kbd-memo"
                data-apn-field="memo-input"
                tabIndex={4}
                placeholder="과제 메모를 입력하세요..."
                value={memoInput}
                onChange={(e) => setMemoInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    focusField('submit-button');
                  }
                }}
              />
            ) : null}
            <div className="cp-apn-kbd-actions">
              <button
                type="button"
                className="cp-apn-kbd-nav-btn"
                aria-label="이전 멤버"
                onClick={() => navigateRelative(-1)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M15 18l-6-6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                className="cp-apn-kbd-submit"
                style={
                  {
                    ['--cp-apn-status' as string]: statusColor(selectedStatus).bg,
                    ['--cp-apn-status-on' as string]: statusColor(selectedStatus).fg,
                  } as React.CSSProperties
                }
                data-apn-field="submit-button"
                tabIndex={5}
                onClick={handleHelperSubmit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleHelperSubmit();
                    focusField('target-input');
                  } else if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault();
                    focusField('target-input');
                  }
                }}
              >
                <span className="cp-apn-kbd-submit-label is-full">
                  {selectedStudents.size > 0
                    ? `선택 ${selectedStudents.size}명 ${withEuroParticle(selectedStatusLabel)} 입력`
                    : `${withEuroParticle(selectedStatusLabel)} 입력`}
                </span>
                <span className="cp-apn-kbd-submit-label is-compact">
                  {selectedStudents.size > 0 ? `선택 ${selectedStudents.size}명 입력` : '입력'}
                </span>
                <span className="cp-apn-kbd-submit-key" aria-hidden>
                  Enter
                </span>
              </button>
              <button
                type="button"
                className="cp-apn-kbd-nav-btn"
                aria-label="다음 멤버"
                onClick={() => navigateRelative(1)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M9 18l6-6-6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div className="cp-apn-kbd-options">
              <div className="cp-apn-kbd-opt">
                <button
                  type="button"
                  className={`cp-apn-switch${autoIncrement ? ' is-on' : ''}`}
                  aria-pressed={autoIncrement}
                  title={autoIncrement ? '연속 번호 입력 활성화' : '연속 번호 입력 비활성화'}
                  onClick={() => setAutoIncrement((v) => !v)}
                />
                <span>연속 번호 입력하기</span>
              </div>
              <div className="cp-apn-kbd-opt">
                <button
                  type="button"
                  className={`cp-apn-switch${pointsEnabled ? ' is-on' : ''}`}
                  aria-pressed={pointsEnabled}
                  title={pointsEnabled ? '포인트 수동 입력 활성화' : '포인트 자동 설정'}
                  onClick={() => {
                    setPointsEnabled((v) => {
                      const next = !v;
                      if (next && selectedStatus === 'pending') {
                        setSelectedStatus('confirmed');
                        setPointsInput(String(basePoints));
                      }
                      return next;
                    });
                  }}
                />
                <span>포인트 입력 활성화</span>
              </div>
              <div className="cp-apn-kbd-opt">
                <button
                  type="button"
                  className={`cp-apn-switch${memoEnabled ? ' is-on' : ''}`}
                  aria-pressed={memoEnabled}
                  title={memoEnabled ? '메모 기능 활성화' : '메모 기능 비활성화'}
                  onClick={() => setMemoEnabled((v) => !v)}
                />
                <span>메모 기능 활성화</span>
              </div>
            </div>
          </div>
        ) : null}
      </section>
      ) : null}
      </div>
      {toastState ? <AutoStatusToast state={toastState} /> : null}
    </div>
  );
};

export default AssignmentCheckPanel;
